import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import AddEditReleaseModal from './AddEditReleaseModal'
import ViewReleaseModal from './ViewReleaseModal'
import FiltersModal, { FilterType } from '../../common/FiltersModal'
import SearchWithFilter from '../../common/SearchWithFilter'
import EmptyPageResults from '../../common/EmptyPageResults'
import LoadingSpinner from '../../common/LoadingSpinner'
import ConfirmationModal from '../../common/ConfirmationModal'
import Dropdown from '../../common/Dropdown'
import SimpleButton from '../../common/SimpleButton'
import GenericTable from '../../common/GenericTable'
import ActionsMenu from '../../common/ActionsMenu'
import { toast } from '../../common/Toast'
import { MenuKebabVerticalIcon, MenuKebabVerticalBackgroundIcon, EnabledIcon, DisabledIcon, InformationIcon } from '../../../icons'
import { firmwareUploadDeletePermission, firmwareUploadWritePermission } from '../../../utils/permissionValidation'
import { loadFirmwareReleases, loadFirmwareReleaseDetails, removeRelease, clearReleaseDetails, clearFirmwareMessages } from '../../../actions/firmware'
import { useWindowSize } from '../../../libs/hooks'
import { useDeepCompareEffect } from '../../../hooks/useDeepCompareEffect'
import { convertTimeWithTAndZ } from '../../../utils/filters/date'
import { groupModelsByDevice } from '../../../utils/functions'
import { firmwareUploadInstructions } from '../../../utils/fixedUrls'
import '../uploads.scss'

const orderingFields = [
  {
    field: 'name',
    order: 'asc',
    translationKey: 'name_asc',
  },
  {
    field: 'name',
    order: 'desc',
    translationKey: 'name_desc',
  },
  {
    field: 'isActive',
    order: 'asc',
    translationKey: 'status_asc',
  },
  {
    field: 'isActive',
    order: 'desc',
    translationKey: 'status_desc',
  },
]

const Releases = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [releasesTableData, setReleasesTableData] = useState([])
  const [selectedFilterModel, setSelectedFilterModel] = useState(null)
  const [searchParam, setSearchParam] = useState(null)
  const [orderBy, setOrderBy] = useState(null)
  const [showSpinner, setShowSpinner] = useState(null)
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(null)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [expandedRelease, setExpandedRelease] = useState(0)
  const [showAddEditReleaseModal, setShowAddEditReleaseModal] = useState(false)
  const [showViewReleaseModal, setShowViewReleaseModal] = useState(false)
  const [releaseIdToDelete, setReleaseIdToDelete] = useState(null)
  const [showDeleteReleaseConfirmModal, setShowDeleteReleaseConfirmModal] = useState(false)
  const statusOptions = ['active', 'inactive']

  useEffect(() => {
    const tableData = props.releases.map((r, index) => {
      return {
        index,
        identifier: r.id,
        releaseName: r.name,
        models: groupModelsByDevice(r.models || []),
        filesNo: r.totalFiles,
        releaseDate: r.date,
        status: r.isActive ? 'active' : 'inactive',
        actions: [
          {
            label: 'view_details',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEditRelease(r.id)
            },
          },
          {
            label: 'edit',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEditRelease(r.id, true)
            },
            requiredPermission: firmwareUploadWritePermission,
          },
          {
            label: 'delete',
            onActionClick: (e) => {
              e.stopPropagation()
              showDeleteConfirmationModal(r.id)
            },
            requiredPermission: firmwareUploadDeletePermission,
            isDisabled: r.isActive,
          },
        ],
      }
    })

    setReleasesTableData(tableData)
  }, [props.releases])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearFirmwareMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)

      props.actions.clearFirmwareMessages()
    }
  }, [props.successMessage])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadReleases(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    let changeValueTimeout = window.setTimeout(
      () => {
        if (shouldApplyFilters) {
          setSelectedPage(1)
          handleLoadReleases(true)
        }

        setShouldApplyFilters(true)
      },
      searchParam ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [searchParam, selectedFilterModel, orderBy])

  const handleLoadReleases = (withReset) => {
    let queryParams = createQueryParams()

    queryParams = {
      ...queryParams,
      startPage: withReset ? 1 : selectedPage,
    }

    props.actions.loadFirmwareReleases(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleSearch = (value) => {
    if (value?.length > 2) {
      setShowSpinner(true)
      setSearchParam(value)
    } else if (value?.length === 0 && searchParam?.length > 0) {
      setShowSpinner(true)
      setSearchParam(value)
    }
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleResetFilters = () => {
    setSelectedFilterModel(null)
  }

  const createQueryParams = () => {
    let queryParams = {}

    if (searchParam) {
      queryParams = { ...queryParams, query: searchParam }
    }

    if (selectedFilterModel) {
      queryParams = { ...queryParams, model: selectedFilterModel }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const selectOrderByOptions = (orderByTranslationKey) => {
    const sortOption = orderingFields.find((field) => field.translationKey === orderByTranslationKey)
    handleSort(sortOption)
  }

  const handleOpenViewEditRelease = (releaseId, isEditModal) => {
    if (!releaseId) {
      return
    }

    props.actions.loadFirmwareReleaseDetails(releaseId).then(() => {
      isEditModal ? setShowAddEditReleaseModal(true) : setShowViewReleaseModal(true)
    })
  }

  const showDeleteConfirmationModal = (id) => {
    setReleaseIdToDelete(id)
    setShowDeleteReleaseConfirmModal(true)
  }

  const handleDeleteRelease = () => {
    setShowDeleteReleaseConfirmModal(false)

    props.actions.removeRelease(releaseIdToDelete)
  }

  const closeModal = () => {
    props.actions.clearReleaseDetails()
    setShowAddEditReleaseModal(false)
    setShowViewReleaseModal(false)
  }

  const toggleShowActionsMenu = (index) => {
    setShowActionsMenu(showActionsMenu === index ? null : index)
  }

  const renderFileStatus = (status) => {
    switch (status) {
      case statusOptions[0]:
        return (
          <div className="status-active">
            <EnabledIcon />
            <p>{t('active')}</p>
          </div>
        )
      case statusOptions[1]:
        return (
          <div className="status-inactive">
            <DisabledIcon />
            <p>{t('inactive')}</p>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <Fragment>
      <div className="uploads-wrapper">
        <div className="actions-wrapper">
          <SearchWithFilter
            onSearch={(e) => handleSearch(e)}
            showFiltersModal={() => setShowFiltersModal(true)}
            onResetFilters={() => handleResetFilters()}
            showCounterBadge={selectedFilterModel}
            counterBadge={selectedFilterModel && 1}
          />
          <SimpleButton className="submit-button" onClick={() => setShowAddEditReleaseModal(true)} requiredPermission={firmwareUploadWritePermission}>
            {t('add_new_release')}
          </SimpleButton>
        </div>
        <div className="information-box-text">
          <div className="icon-wrapper">
            <InformationIcon />
          </div>
          <div className="information-wrapper">
            <div className="title">
              {`${t('firmware_context.upload_instructions')} `}
              <span>
                <a href={firmwareUploadInstructions} target="_blank" rel="noreferrer" className="external-link">
                  {t('document_context.click_here')}
                </a>
              </span>
            </div>
          </div>
        </div>
        {isMobile && releasesTableData.length > 0 && (
          <div className="order-by-mobile-wrapper">
            <Dropdown
              className="dropdown-input"
              placeholder={t('select_ordering')}
              options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
              initialSelectedKey={orderBy?.translationKey}
              onChange={(orderBy) => selectOrderByOptions(orderBy.key)}
            />
          </div>
        )}
        {!isMobile && releasesTableData.length > 0 && (
          <GenericTable
            data={releasesTableData}
            headers={[
              {
                title: t('firmware_context.release_name'),
                orderKey: 'name',
              },
              {
                title: t('monitoring.models'),
              },
              {
                title: t('files'),
              },
              {
                title: t('date'),
              },
              {
                title: t('status'),
                orderKey: 'isActive',
              },
              {
                title: '',
              },
            ]}
            keys={['releaseName', 'models', 'filesNo', 'releaseDate', 'status', 'action']}
            keyRenderer={{
              releaseDate: (item) => (item.releaseDate ? convertTimeWithTAndZ(item.releaseDate) : ''),
              status: (item) => {
                return <div className="status-wrapper">{renderFileStatus(item.status)}</div>
              },
              action: (item) => {
                return (
                  <div
                    className="table-actions-wrapper"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleShowActionsMenu(item['index'])
                    }}
                  >
                    {showActionsMenu === item['index'] ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === item['index'] && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />
                    )}
                  </div>
                )
              },
            }}
            onRowClick={(release) => handleOpenViewEditRelease(release.identifier)}
            activeSort={orderBy}
            onSort={(orderingField) => handleSort(orderingField)}
            isLoading={showSpinner}
          />
        )}
        {isMobile &&
          releasesTableData.map((release, index) => (
            <div key={index} className="card" onClick={() => setExpandedRelease(index)}>
              <div className={'card-item' + (expandedRelease !== index ? ' align-center' : '')}>
                <div className="card-item-title">{t('firmware_context.release_name')}</div>
                <div className={'card-item-body' + (expandedRelease !== index ? ' align-center' : '')}>
                  <div>{release.releaseName}</div>
                  <div
                    className="card-actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowActionsMenu(index)
                    }}
                  >
                    {showActionsMenu === index ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === index && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={release.actions} isLoading={showSpinner} />
                    )}
                  </div>
                </div>
              </div>
              {expandedRelease === index && (
                <>
                  <div className="card-item">
                    <div className="card-item-title">{t('monitoring.models')}</div>
                    <div className="card-item-body">{release.models}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('files')}</div>
                    <div className="card-item-body">{release.filesNo}</div>
                  </div>

                  <div className="card-item">
                    <div className="card-item-title">{t('date')}</div>
                    <div className="card-item-body">{release.releaseDate ? convertTimeWithTAndZ(release.releaseDate) : ''}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('status')}</div>
                    <div className="card-item-body card-item-body-status">{renderFileStatus(release.status)}</div>
                  </div>
                </>
              )}
            </div>
          ))}
        {!showSpinner && !isFirstLoad && releasesTableData.length === 0 && (
          <EmptyPageResults title={t('firmware_context.no_firmware_found')} subtitle={t('cannot_find_matching_search')} />
        )}
        {canLoadMore && (
          <div className="buttons-wrapper">
            <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
              {t('load_more')}
            </SimpleButton>
          </div>
        )}
      </div>
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('model'),
              type: FilterType.singleSelect,
              dataset: Array.from(new Set(props.prefixes.map((p) => p.models).flat())),
              input: selectedFilterModel,
              output: (filterModel) => {
                setSelectedFilterModel(filterModel)
              },
            },
          ]}
          shouldFilter={(shouldApply) => setShouldApplyFilters(shouldApply)}
          resetFilters={() => handleResetFilters()}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {showSpinner && (
        <div className={'spinner-wrapper' + (showAddEditReleaseModal ? ' spinner-locked' : '')}>
          <LoadingSpinner />
        </div>
      )}
      {showDeleteReleaseConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowDeleteReleaseConfirmModal(false)}
          onAccept={() => handleDeleteRelease()}
          onCancel={() => setShowDeleteReleaseConfirmModal(false)}
          message={t('firmware_context.confirm_delete_release')}
          acceptButtonText={t('delete')}
          isWarning
          requiredPermission={firmwareUploadDeletePermission}
        />
      )}
      {showAddEditReleaseModal && <AddEditReleaseModal closeModal={closeModal} showSpinner={showSpinner} setShowSpinner={setShowSpinner} />}
      {showViewReleaseModal && (
        <ViewReleaseModal
          showSpinner={showSpinner}
          setShowSpinner={setShowSpinner}
          handleEditRelease={() => {
            setShowViewReleaseModal(false)
            setShowAddEditReleaseModal(true)
          }}
          closeModal={closeModal}
        />
      )}
    </Fragment>
  )
}

function stateToProps({ firmware }) {
  return {
    releases: firmware?.firmwareReleases || [],
    prefixes: firmware?.prefixes || [],
    types: firmware?.types || [],
    isLoading: firmware?.isLoading,
    canLoadMore: firmware?.canLoadMoreFirmwareReleases,
    successMessage: firmware?.successMessage,
    errorMessage: firmware?.errorMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadFirmwareReleases,
        loadFirmwareReleaseDetails,
        removeRelease,
        clearReleaseDetails,
        clearFirmwareMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(Releases)

